// Plan 23a, Task 4.1 — Slider primitive
// CSS-transitions only. NO gsap usage (per spec).

import type { UIComponentHandle, UIComponentConfig } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface SliderOpts {
  min: number;
  max: number;
  /** Initial value — defaults to `min`. */
  value?: number;
  /** Step granularity — defaults to 1. */
  step?: number;
  /** Called only on user pointer interaction (NOT on programmatic setValue). */
  onInput?: (value: number) => void;
}

export interface SliderHandle extends UIComponentHandle {
  /** Current numeric value. */
  readonly value: number;
  /**
   * Programmatically set value. Clamps to [min, max], rounds to step.
   * Does NOT fire onInput.
   */
  setValue(v: number): void;
  /** Apply / remove gamepad-focus styling on the thumb. */
  setGamepadFocus(focused: boolean): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Clamp `v` to [lo, hi]. */
function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * Round `v` to the nearest multiple of `step`.
 * Floating-point safe via toFixed rounding.
 */
function roundToStep(v: number, step: number): number {
  if (step <= 0) return v;
  const inv = 1 / step;
  return Math.round(v * inv) / inv;
}

/**
 * Compute fill percentage width string from a value.
 * Returns e.g. "50%" with no trailing decimals when exact.
 */
function fillPercent(v: number, min: number, max: number): string {
  const range = max - min;
  if (range === 0) return '0%';
  const pct = ((v - min) / range) * 100;
  return `${pct}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createSlider(config: UIComponentConfig & SliderOpts): SliderHandle {
  const { min, max, value: initialValue = min, step = 1, onInput, mount } = config;

  // Internal state
  let currentValue = clamp(roundToStep(initialValue, step), min, max);
  let disposed = false;
  let dragging = false;

  // ── Build DOM ──────────────────────────────────────────────────────────────

  // Track (outer container)
  const track = document.createElement('div');
  track.className = 'ui-slider';
  track.style.cssText = [
    // color.ui.panel-bg → #1E2332
    `background: var(--color-ui-panel-bg, #1E2332)`,
    `height: 6px`,
    // border-radius.pill → 9999px
    `border-radius: var(--border-radius-pill, 9999px)`,
    `position: relative`,
    `width: 100%`,
    `cursor: pointer`,
    `box-sizing: border-box`,
  ].join('; ');

  // Fill
  const fill = document.createElement('div');
  fill.className = 'ui-slider__fill';
  fill.dataset.sliderFill = '';
  fill.style.cssText = [
    // color.brand.amber → #E8A020
    `background: var(--color-brand-amber, #E8A020)`,
    `height: 100%`,
    `border-radius: var(--border-radius-pill, 9999px)`,
    `position: absolute`,
    `top: 0`,
    `left: 0`,
    `width: ${fillPercent(currentValue, min, max)}`,
    `pointer-events: none`,
    // CSS transition on width (animation.duration.fast = 150ms)
    `transition: width var(--animation-duration-fast, 150ms)`,
  ].join('; ');

  // Thumb — 20px circle, absolutely positioned, vertically centred on track
  const thumb = document.createElement('div');
  thumb.className = 'ui-slider__thumb';
  thumb.dataset.sliderThumb = '';
  thumb.style.cssText = [
    `width: 20px`,
    `height: 20px`,
    // border-radius.circle → 50%
    `border-radius: var(--border-radius-circle, 50%)`,
    // color.brand.amber fill + 2px solid color.brand.amber-dark border
    `background: var(--color-brand-amber, #E8A020)`,
    `border: 2px solid var(--color-brand-amber-dark, #C4841C)`,
    `position: absolute`,
    `top: 50%`,
    `transform: translateY(-50%)`,
    // left is set by updateUI()
    `left: ${fillPercent(currentValue, min, max)}`,
    `cursor: grab`,
    `box-sizing: border-box`,
    // CSS transitions: transform on hover/drag, box-shadow on drag
    `transition: transform var(--animation-duration-fast, 150ms), box-shadow var(--animation-duration-fast, 150ms), border var(--animation-duration-fast, 150ms)`,
    `user-select: none`,
    `touch-action: none`,
  ].join('; ');

  track.appendChild(fill);
  track.appendChild(thumb);

  // ── Mount helper ────────────────────────────────────────────────────────────

  if (mount) {
    mount.appendChild(track);
  }

  // ── Render update ──────────────────────────────────────────────────────────

  function updateUI(): void {
    const pct = fillPercent(currentValue, min, max);
    fill.style.width = pct;
    // Thumb left: positioned at pct of track width, then shifted left by half
    // thumb width. In pure CSS we can't do calc(pct - 10px) here easily via JS,
    // but we set left = pct and transform handles vertical centering; horizontal
    // offset is done with marginLeft.
    thumb.style.left = pct;
    // Offset thumb centre: translate(-50%) horizontally so thumb is centred on
    // the fill endpoint. Combine with vertical translateY(-50%).
    thumb.style.transform = `translateX(-50%) translateY(-50%)`;
  }

  // Apply initial positions
  updateUI();

  // ── Hover state ────────────────────────────────────────────────────────────

  function onThumbMouseEnter(): void {
    if (disposed || dragging) return;
    thumb.style.width = '24px';
    thumb.style.height = '24px';
  }

  function onThumbMouseLeave(): void {
    if (disposed || dragging) return;
    thumb.style.width = '20px';
    thumb.style.height = '20px';
  }

  thumb.addEventListener('mouseenter', onThumbMouseEnter);
  thumb.addEventListener('mouseleave', onThumbMouseLeave);

  // ── Drag — pointer events ──────────────────────────────────────────────────

  function computeValueFromClientX(clientX: number): number {
    const rect = track.getBoundingClientRect();
    const trackWidth = rect.width;
    if (trackWidth === 0) return currentValue;

    const relX = clientX - rect.left;
    const ratio = clamp(relX / trackWidth, 0, 1);
    const raw = min + ratio * (max - min);
    return clamp(roundToStep(raw, step), min, max);
  }

  function onPointerMove(e: PointerEvent): void {
    if (!dragging || disposed) return;
    const newVal = computeValueFromClientX(e.clientX);
    currentValue = newVal;
    updateUI();
    onInput?.(newVal);
  }

  function onPointerUp(): void {
    if (!dragging) return;
    dragging = false;
    thumb.style.cursor = 'grab';
    // Remove dragging styles
    thumb.style.boxShadow = '';
    // Revert size if mouse is not over thumb anymore
    thumb.style.width = '20px';
    thumb.style.height = '20px';
  }

  function onTrackPointerDown(e: PointerEvent): void {
    if (disposed || dragging) return;
    if (e.button !== 0) return;
    e.preventDefault();
    dragging = true;
    thumb.style.cursor = 'grabbing';
    // Dragging state: shadow.glow-amber
    thumb.style.boxShadow = `var(--shadow-glow-amber, 0 0 12px rgba(232,160,32,0.50))`;
    // Enlarge during drag
    thumb.style.width = '24px';
    thumb.style.height = '24px';

    const newVal = computeValueFromClientX(e.clientX);
    currentValue = newVal;
    updateUI();
    onInput?.(newVal);

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp, { once: true });
  }

  track.addEventListener('pointerdown', onTrackPointerDown);

  // ── Gamepad focus ──────────────────────────────────────────────────────────

  function setGamepadFocus(focused: boolean): void {
    if (disposed) return;
    if (focused) {
      thumb.classList.add('ui-slider__thumb--gamepad-focus');
      // border.focus-gamepad → 3px solid #E8A020
      thumb.style.border = `var(--border-focus-gamepad, 3px solid #E8A020)`;
    } else {
      thumb.classList.remove('ui-slider__thumb--gamepad-focus');
      thumb.style.border = `2px solid var(--color-brand-amber-dark, #C4841C)`;
    }
  }

  // ── setValue (programmatic — does NOT fire onInput) ────────────────────────

  function setValue(v: number): void {
    if (disposed) return;
    currentValue = clamp(roundToStep(v, step), min, max);
    updateUI();
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Stop any active drag by removing document listeners
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);

    // Remove element-level listeners
    track.removeEventListener('pointerdown', onTrackPointerDown);
    thumb.removeEventListener('mouseenter', onThumbMouseEnter);
    thumb.removeEventListener('mouseleave', onThumbMouseLeave);

    // Detach from DOM
    if (track.parentNode) {
      track.parentNode.removeChild(track);
    }
  }

  // ── value getter ────────────────────────────────────────────────────────────

  return {
    element: track,
    get value() {
      return currentValue;
    },
    setValue,
    setGamepadFocus,
    dispose,
  };
}
